/**
 * Unified dashboard settings dialog (single "settings" owner per review A11):
 * details (name + description), parameters (S10), and playlist config (S13) —
 * reachable from both the workspace tab's kebab and the Object Explorer
 * node's "Rename" item.
 *
 * COMMIT MODEL (S24 UX review): the Details block applies on **Save**; the
 * Parameters and Playlist sections commit the instant you change them. That
 * split used to be invisible — a user clicking "Cancel" believed their
 * parameter/playlist edits had been reverted. Each instant section now
 * carries a persistent "Saves immediately" line that flips to "Saved" for a
 * moment after every commit, and the secondary footer button only says
 * "Cancel" while unsaved details actually exist (else "Close") — with a
 * tooltip naming exactly what it discards.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { Dashboard, DashboardParameter } from '../types/dashboardTypes';
import { readDashboardDescription, saveDashboardDetails } from '../services/dashboardDetails';
import { InstantSaveSection, SAVED_FLASH_MS } from './InstantSaveSection';
import { ParameterSettingsSection } from './params/ParameterSettingsSection';
import { PlaylistSettingsSection } from './PlaylistSettingsSection';

type DashboardSettingsDialogProps = {
  dashboard: Dashboard;
  /** All dashboards — the playlist picker offers everything but itself. */
  dashboards: Dashboard[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRename: (name: string) => void;
  onSaveParameters: (parameters: DashboardParameter[]) => void;
  onSavePlaylist: (playlist: Dashboard['playlist']) => void;
};

export function DashboardSettingsDialog({
  dashboard,
  dashboards,
  open,
  onOpenChange,
  onRename,
  onSaveParameters,
  onSavePlaylist,
}: DashboardSettingsDialogProps) {
  const [name, setName] = useState(dashboard.name);
  const [description, setDescription] = useState(() => readDashboardDescription(dashboard));
  const [savedSection, setSavedSection] = useState<'parameters' | 'playlist' | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentDescription = readDashboardDescription(dashboard);
  useEffect(() => {
    if (open) {
      setName(dashboard.name);
      setDescription(currentDescription);
    }
  }, [open, dashboard.name, currentDescription]);

  useEffect(
    () => () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    },
    [],
  );

  const flashSaved = (section: 'parameters' | 'playlist') => {
    setSavedSection(section);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedSection(null), SAVED_FLASH_MS);
  };

  const trimmed = name.trim();
  const nextDescription = description.trim() || undefined;
  const nameChanged = trimmed !== dashboard.name;
  const descriptionChanged = nextDescription !== (currentDescription || undefined);
  const hasUnsavedDetails = nameChanged || descriptionChanged;
  const canSave = trimmed.length > 0 && hasUnsavedDetails;

  const save = () => {
    if (!canSave) return;
    // Name-only edits keep the caller's rename path (its own replay + toasts).
    if (descriptionChanged) void saveDashboardDetails(dashboard, trimmed, nextDescription);
    else onRename(trimmed);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dashboard-settings-dialog">
        <DialogHeader>
          <DialogTitle>Dashboard settings</DialogTitle>
          <DialogDescription>Rename this dashboard and manage its configuration.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dashboard-settings-name">Name</Label>
            <Input
              id="dashboard-settings-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && save()}
              // Explicit foreground: the value must never read as placeholder
              // grey in dark mode (S24 review finding).
              className="text-foreground"
              data-testid="dashboard-settings-name-input"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dashboard-settings-description">Description (optional)</Label>
            <Textarea
              id="dashboard-settings-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What this dashboard is for — travels with exports and shared definitions."
              className="min-h-16 resize-none text-foreground"
              data-testid="dashboard-settings-description-input"
            />
          </div>
          <InstantSaveSection
            saved={savedSection === 'parameters'}
            testId="param-settings-save-hint"
          >
            <ParameterSettingsSection
              parameters={dashboard.parameters}
              onSave={(parameters) => {
                onSaveParameters(parameters);
                flashSaved('parameters');
              }}
            />
          </InstantSaveSection>
          <InstantSaveSection saved={savedSection === 'playlist'} testId="playlist-save-hint">
            <PlaylistSettingsSection
              dashboard={dashboard}
              dashboards={dashboards}
              onSave={(playlist) => {
                onSavePlaylist(playlist);
                flashSaved('playlist');
              }}
            />
          </InstantSaveSection>
        </div>
        <DialogFooter>
          {/* "Cancel" only when there is genuinely something to discard — and
              its tooltip names the scope, since parameters/playlist are
              already persisted by then (S24 review). */}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            title={
              hasUnsavedDetails
                ? 'Discards the unsaved name and description — parameters and playlist are already saved'
                : undefined
            }
            data-testid="dashboard-settings-close-button"
          >
            {hasUnsavedDetails ? 'Cancel' : 'Close'}
          </Button>
          <span
            title={
              canSave
                ? 'Save the name and description'
                : trimmed.length === 0
                  ? 'Enter a name to save'
                  : 'Change the name or description to save'
            }
          >
            <Button
              onClick={save}
              disabled={!canSave}
              className="disabled:bg-muted disabled:text-muted-foreground"
              data-testid="dashboard-settings-save-button"
            >
              Save
            </Button>
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
